/**
 * +field: what a level-up gave (the banner's gains list): +health / +power (progression
 * heroLevelStats), each new ability ("added to your bar", with its icon), talent points, and
 * places that open at the new level (a field's band, a dungeon entrance's minimum level).
 * Pure: reads the content registry only.
 */
import type { ClassId, ContentRegistry, DungeonDef } from '@keystone/sim';
import { GRID_POINTS_PER_LEVEL, LEVEL_CAP, heroLevelStats, unlockedBetween } from '@keystone/progression'; // +grid: GRID_POINTS_PER_LEVEL

export interface LevelGain {
  kind: 'stat' | 'ability' | 'talent' | 'access';
  text: string;
  /** Ability id (its icon) for 'ability'. */
  abilityId?: string;
}

export interface LevelUpSummary {
  from: number;
  to: number;
  gained: number;
  /** Level cap reached. */
  capped: boolean;
  gains: LevelGain[];
  /** New abilities (for the action-bar glow). */
  abilities: string[];
}

const plural = (n: number, one: string, many = `${one}s`): string => `${n} ${n === 1 ? one : many}`;

export function levelUpSummary(content: Pick<ContentRegistry, 'class' | 'ability' | 'bundle'>, classId: ClassId, from: number, to: number): LevelUpSummary {
  const gained = Math.max(0, to - from);
  const gains: LevelGain[] = [];
  const cls = content.class(classId);
  const a = heroLevelStats(classId, from, cls as never);
  const b = heroLevelStats(classId, to, cls as never);
  if (b.maxHp > a.maxHp) gains.push({ kind: 'stat', text: `+${b.maxHp - a.maxHp} Health` });
  if (b.power > a.power) gains.push({ kind: 'stat', text: `+${b.power - a.power} Power` });
  const abilities = unlockedBetween(classId, from, to, cls as never);
  for (const id of abilities) {
    const ab = content.ability(id);
    const name = ab?.name ?? id;
    // +basekits: the one-line "how to use" (ui.hint) when the ability has one.
    const hint = ab?.ui?.hint;
    gains.push({ kind: 'ability', text: hint ? `New ability: ${name} — ${hint}` : `New ability: ${name} — added to your bar`, abilityId: id });
  }
  if (gained > 0) gains.push({ kind: 'talent', text: `+${plural(gained * GRID_POINTS_PER_LEVEL, 'grid point')} — open the Waystone Grid (N)` }); // +grid
  for (const t of accessOpened(content.bundle.dungeons, from, to)) gains.push({ kind: 'access', text: t });
  return { from, to, gained, capped: to >= LEVEL_CAP, gains, abilities };
}

/** "The Hollow Barrow is open to you" lines for places whose entry level lies in (from, to]. */
export function accessOpened(dungeons: readonly DungeonDef[], from: number, to: number): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  const byId = new Map(dungeons.map((d) => [d.id, d]));
  for (const d of dungeons) {
    const f = d.field;
    if (!f) continue;
    // A field opens at the bottom of its band (the starting field is open from the start).
    const lo = f.levelBand[0];
    if (f.kind === 'field' && lo > 1 && lo > from && lo <= to && !seen.has(d.id)) {
      seen.add(d.id);
      out.push(`${d.name} is open to you (levels ${f.levelBand[0]}–${f.levelBand[1]})`);
    }
    for (const p of f.portals) {
      if (p.kind !== 'dungeon' || p.minLevel === undefined || seen.has(p.to)) continue;
      if (p.minLevel > from && p.minLevel <= to) {
        seen.add(p.to);
        out.push(`${byId.get(p.to)?.name ?? p.name} is open to you`);
      }
    }
  }
  return out;
}
